import { IconDental, IconEyeglass, IconStethoscope } from '@tabler/icons-react';
import { isDefined } from '@zorro/common-utils';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Grid, Space } from '@zorro/web/design-system';
import {
  getFamilyUnitFromWhoIsEnrolled,
  getWhoIsEnrolled,
  useMonolithQuery,
} from '@zorro/web/ui-utils';

import { EnrollmentReviewAllowanceTooltip } from './EnrollmentReviewAllowanceTooltip';
import { EnrollmentReviewCardContent } from './EnrollmentReviewCardContent';
import { EnrollmentReviewCardPlanDetails } from './EnrollmentReviewCardPlanDetails';

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  withPlanNameAndInsuredColumns?: boolean;
  highlightedBenefitId?: string;
  withOnboardingAllowance?: boolean;
};

export const EnrollmentReviewCard = ({
  employeeId,
  onboardingPeriodId,
  withPlanNameAndInsuredColumns = false,
  highlightedBenefitId,
  withOnboardingAllowance = false,
}: Props) => {
  const { data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  const insured = benefits?.majorMedicalBenefit?.insuredPeople;
  const whoIsEnrolled = insured ? getWhoIsEnrolled(insured) : [];
  const { familyUnit } = insured
    ? getFamilyUnitFromWhoIsEnrolled(insured, new Set(whoIsEnrolled))
    : {};

  const { data: employerContribution } = useMonolithQuery({
    method: 'contributionModelsControllerFindEmployeesEmployerContribution',
    params: [onboardingPeriodId, familyUnit, insured?.dependents?.length],
  });
  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
    enabled: withOnboardingAllowance,
  });

  const stipend = withOnboardingAllowance
    ? (onboardingPeriod?.allowance ?? employerContribution?.stipend)
    : employerContribution?.stipend;

  const { data: enrollmentReview } = useMonolithQuery({
    method: 'onboardingPeriodsControllerGetEnrollmentReviewForSubmission',
    params: isDefined(stipend) && [onboardingPeriodId, stipend],
  });

  const planNameInsuredColumnSpan = withPlanNameAndInsuredColumns ? 3 : 8;

  if (!enrollmentReview) {
    return null;
  }

  const withPlanDetails =
    onboardingPeriod?.enrollmentIsDentalEnabled ||
    onboardingPeriod?.enrollmentIsVisionEnabled;

  return (
    <EnrollmentReviewCardContent
      stipend={stipend}
      totals={enrollmentReview}
      allowanceTooltip={
        <EnrollmentReviewAllowanceTooltip
          onboardingPeriodId={onboardingPeriodId}
        />
      }
      planDetails={
        withPlanDetails && (
          <>
            <Grid.Col>
              <Space h="sm" />
            </Grid.Col>
            {enrollmentReview.majorMedicalBenefits.map(
              ({ benefitId, ...benefit }) => (
                <EnrollmentReviewCardPlanDetails
                  {...benefit}
                  withPlanNameAndInsuredColumns={withPlanNameAndInsuredColumns}
                  planNameInsuredColumnSpan={planNameInsuredColumnSpan}
                  icon={IconStethoscope}
                  key={benefitId}
                  isHighlighted={benefitId === highlightedBenefitId}
                />
              )
            )}
            {enrollmentReview.dentalBenefits.map(
              ({ benefitId, ...benefit }) => (
                <EnrollmentReviewCardPlanDetails
                  {...benefit}
                  withPlanNameAndInsuredColumns={withPlanNameAndInsuredColumns}
                  planNameInsuredColumnSpan={planNameInsuredColumnSpan}
                  icon={IconDental}
                  key={benefitId}
                  isHighlighted={benefitId === highlightedBenefitId}
                />
              )
            )}
            {enrollmentReview.visionBenefits.map(
              ({ benefitId, ...benefit }) => (
                <EnrollmentReviewCardPlanDetails
                  {...benefit}
                  withPlanNameAndInsuredColumns={withPlanNameAndInsuredColumns}
                  planNameInsuredColumnSpan={planNameInsuredColumnSpan}
                  icon={IconEyeglass}
                  key={benefitId}
                  isHighlighted={benefitId === highlightedBenefitId}
                />
              )
            )}
            <Grid.Col mt="sm">
              <Divider />
            </Grid.Col>
          </>
        )
      }
    />
  );
};
